import axios from "axios";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Bell, Trash2, X, CheckCircle2 } from "lucide-react";

export default function NotificationFront() {
  const session_id = Cookies.get("jwt");
  const [dataMessageInbox, setDataMessageInbox] = useState([]);
  const [waitSelectedMessage, setWaitSelectedMessage] = useState(false);
  const [selectedMessageID, setSelectedMessageID] = useState([]);

  function parseTimestamp(timestamp) {
    return new Date(timestamp);
  }

  function formatDateToDDMMYYYY(date) {
    const day = ("0" + date.getUTCDate()).slice(-2);
    const month = ("0" + (date.getUTCMonth() + 1)).slice(-2);
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  }

  function getInBoxMessage() {
    axios
      .get(`${process.env.REACT_APP_API}/inbox/getInbox`, {
        headers: { Authorization: `Bearer ${session_id}` },
      })
      // API คืน object ของ Sequelize มาทั้งก้อน (มี dataValues/_previousDataValues ติดมาด้วย)
      // ฟิลด์จริง เช่น message_id / sent_date / readed อยู่ใน dataValues ถ้าอ่านจากชั้นนอกจะได้ undefined
      // ทำให้ key ของ list ซ้ำกัน วันที่ไม่ขึ้น และทุกข้อความถูกนับเป็นยังไม่อ่าน
      .then((res) => setDataMessageInbox((res.data.Inbox || []).map((m) => m?.dataValues ?? m)))
      .catch(console.error);
  }

  useEffect(() => {
    getInBoxMessage();
  }, []);

  useEffect(() => {
    if (session_id === undefined) {
      window.location.replace("/login");
    }
  }, []);

  function handleClearAllMessageInbox() {
    axios
      .post(
        `${process.env.REACT_APP_API}/inbox/deleteMessage`,
        { messageIds: selectedMessageID },
        { headers: { Authorization: `Bearer ${session_id}` } }
      )
      .then(() => {
        getInBoxMessage();
        setWaitSelectedMessage(false);
        setSelectedMessageID([]);
      })
      .catch(console.error);
  }

  function handleRemoveInboxNotification() {
    setWaitSelectedMessage(!waitSelectedMessage);
    setSelectedMessageID([]);
  }

  function handleSelectedInbox(id) {
    if (selectedMessageID.includes(id)) {
      setSelectedMessageID(selectedMessageID.filter((m) => m !== id));
    } else {
      setSelectedMessageID([...selectedMessageID, id]);
    }
  }

  function handleSaveIDMessage(idMessage) {
    sessionStorage.setItem("messageID", idMessage);
  }

  const unreadCount = dataMessageInbox.filter((m) => !m.readed).length;

  return (
    <div className="min-h-screen bg-gray-50 lg:pt-20">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4 flex items-center gap-3"
        >
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">กล่องข้อความ</p>
            <h1 className="text-lg font-bold text-gray-900">การแจ้งเตือน</h1>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <span className="text-xs px-2.5 py-1 bg-red-50 text-red-500 font-semibold rounded-full border border-red-100">
                {unreadCount} ใหม่
              </span>
            )}
            <span className="text-xs px-2.5 py-1 bg-blue-50 text-blue-600 font-semibold rounded-full">
              {dataMessageInbox.length} รายการ
            </span>
          </div>
        </motion.div>

        {/* Action bar */}
        {dataMessageInbox.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.04 }}
            className="flex items-center justify-end gap-2"
          >
            {waitSelectedMessage && selectedMessageID.length > 0 && (
              <button
                onClick={handleClearAllMessageInbox}
                className="flex items-center gap-1.5 px-3 py-2 bg-red-500 text-white text-xs font-semibold rounded-xl hover:bg-red-600 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                ลบที่เลือก ({selectedMessageID.length})
              </button>
            )}
            <button
              onClick={handleRemoveInboxNotification}
              className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-600 text-xs font-semibold rounded-xl hover:bg-gray-50 transition-colors"
            >
              {waitSelectedMessage ? (
                <><X className="w-3.5 h-3.5" />ยกเลิก</>
              ) : (
                <><Trash2 className="w-3.5 h-3.5" />แก้ไข</>
              )}
            </button>
          </motion.div>
        )}

        {/* Notification list */}
        {dataMessageInbox.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center justify-center py-16 gap-3 text-gray-400"
          >
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center">
              <Bell className="w-7 h-7 opacity-30" />
            </div>
            <p className="text-sm">ไม่มีการแจ้งเตือน</p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 divide-y divide-gray-50 overflow-hidden"
          >
            {dataMessageInbox
              .slice()
              .reverse()
              .map((data, index) => {
                const formattedDate = formatDateToDDMMYYYY(parseTimestamp(data.sent_date));
                const isSelected = selectedMessageID.includes(data.message_id);

                return (
                  <motion.div
                    key={data.message_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.04 }}
                    className={`relative transition-colors ${isSelected ? "bg-blue-50" : "hover:bg-gray-50"}`}
                    onClick={() => waitSelectedMessage && handleSelectedInbox(data.message_id)}
                  >
                    <Link
                      to={waitSelectedMessage ? undefined : `/notifications/${data.message_id}`}
                      onClick={() => !waitSelectedMessage && handleSaveIDMessage(data.message_id)}
                      className="flex items-start gap-4 px-5 py-4"
                    >
                      {/* Unread dot / checkbox */}
                      <div className="flex-shrink-0 mt-1">
                        {waitSelectedMessage ? (
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? "border-blue-600 bg-blue-600" : "border-gray-300"}`}>
                            {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                          </div>
                        ) : (
                          <div className={`w-2.5 h-2.5 rounded-full mt-1 ${!data.readed ? "bg-red-500" : "bg-gray-200"}`} />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className={`text-sm font-semibold truncate ${!data.readed ? "text-gray-900" : "text-gray-600"}`}>
                            {data.title}
                          </p>
                          <time className="text-xs text-gray-400 flex-shrink-0">{formattedDate}</time>
                        </div>
                        <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">
                          {data.description.slice(0, 100)}...
                        </p>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
          </motion.div>
        )}

        <div className="h-8" />
      </div>
    </div>
  );
}
